import { FunctionComponent, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import LogoFull from "../../../components/logo-full"
import { useResetPasswordMutation } from "../../../app/services/meseeknaApiV2"
import BaseButton from "../../../components/Button/base-button"
import zxcvbn from "zxcvbn"

type ResetPasswordFormProps = object

const ResetPasswordForm: FunctionComponent<ResetPasswordFormProps> = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [passwordFeedback, setPasswordFeedback] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  // Get token and email from URL parameters
  const token = searchParams.get("l") || ""
  const email = searchParams.get("m") || ""

  const [resetPassword] = useResetPasswordMutation()

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    const result = zxcvbn(value)
    setPasswordStrength(result.score)
    setPasswordFeedback(
      result.feedback.warning || result.feedback.suggestions[0] || "",
    )
  }

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
        return "bg-red-500"
      case 1:
        return "bg-orange-500"
      case 2:
        return "bg-yellow-500"
      case 3:
        return "bg-blue-500"
      case 4:
        return "bg-green-500"
      default:
        return "bg-gray-200"
    }
  }

  const validatePassword = () => {
    if (password.length < 8) {
      setError("Password must be at least 8 characters long")
      return false
    }

    if (passwordStrength < 3) {
      setError("Please choose a stronger password")
      return false
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return false
    }

    return true
  }

  const handleResetPassword = async () => {
    setError("")

    // Validate the token and email
    if (!token || !email) {
      setError("Invalid reset link. Please request a new password reset.")
      return
    }

    // Validate password
    if (!validatePassword()) {
      return
    }

    setIsLoading(true)

    try {
      const response = await resetPassword({
        token,
        email,
        password,
      }).unwrap()

      if (response.code === 200) {
        setIsSuccess(true)
        // After 3 seconds, redirect to login
        setTimeout(() => {
          navigate("/login")
        }, 3000)
      } else {
        setError(response.message || "Failed to reset password")
      }
    } catch (error: unknown) {
      const errorResponse = error as {
        data?: { message?: string }
        status?: number
      }
      console.error("Error resetting password:", error)

      if (errorResponse.data?.message) {
        setError(errorResponse.data.message)
      } else if (errorResponse.status === 400) {
        setError(
          "Invalid or expired reset token. Please request a new password reset.",
        )
      } else {
        setError(
          "An error occurred while resetting your password. Please try again.",
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <main className="w-[460px] flex flex-col items-center justify-start gap-[32px] text-left text-sm text-gray-900 font-text-md-regular1">
      <div className="self-stretch flex flex-col items-center justify-start gap-[24px] text-center font-text-md-regular">
        <LogoFull
          dimensions="/v3/assets/meseekna-logo@2x.png"
          logoWrapWidth="189px"
          logoWrapHeight="31.5px"
          image1Width="188.61px"
          image1Height="30.02px"
        />
        <div className="self-stretch flex flex-col items-start justify-start gap-[12px]">
          <h1 className="m-0 self-stretch relative text-2xl leading-[48px] font-semibold font-inherit">
            Reset your password
          </h1>

          {isSuccess ? (
            <div className="self-stretch flex flex-col items-center justify-center gap-[16px] mt-4">
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                Password reset successful! You will be redirected to the login
                page.
              </div>
            </div>
          ) : (
            <div className="self-stretch flex flex-col items-center justify-center gap-[20px]">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative w-full text-left">
                  <span>{error}</span>
                </div>
              )}

              <div className="w-full">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  New Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className="box-border w-full rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] py-2.5 px-3.5 text-base text-gray-500 border border-gray-300"
                  required
                />
                {password && (
                  <div className="mt-2">
                    <div className="h-2 w-full bg-gray-200 rounded-full">
                      <div
                        className={`h-full rounded-full transition-all ${getPasswordStrengthColor()}`}
                        style={{
                          width: `${(passwordStrength + 1) * 20}%`,
                        }}
                      />
                    </div>
                    {passwordFeedback && (
                      <p className="mt-1 text-sm text-gray-500 text-left">
                        {passwordFeedback}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="w-full">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="box-border w-full rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] py-2.5 px-3.5 text-base text-gray-500 border border-gray-300"
                  required
                />
              </div>

              <BaseButton
                variant="primary"
                className="self-stretch mt-8"
                onClick={handleResetPassword}
                disabled={isLoading}>
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Resetting Password...
                  </div>
                ) : (
                  "Reset Password"
                )}
              </BaseButton>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

export default ResetPasswordForm
